/**
 * Coded by www.techhighway.ca
 */

import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

// @mui components
import AppBar from "@mui/material/AppBar";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import UserAvatar from "utility/user-avatar";

import AuthService from "services/auth-service";
import "./styles.css";

import {
  navbar,
  navbarContainer,
  navbarIconButton,
  navbarMobileMenu,
  navbarRow,
} from "components/Navbars/DashboardNavbar/styles";

import {
  AuthContext,
  setMiniSidenav,
  setTransparentNavbar,
  useMaterialUIController,
} from "context";

function DashboardNavbar({ absolute=false, light=false, isMini=false, breadcrumbsTitle }) {
  const authContext = useContext(AuthContext);
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;

  const name = `${authContext.userDetail.first_name} ${authContext.userDetail.last_name}`;

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => {
    setMiniSidenav(dispatch, !miniSidenav);
  };

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : white.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? white.main : white.main;
      }
      return colorValue;
    },
  });

  const handleLogOut = async () => {
    await AuthService.logout();
    authContext.logout();
  };

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      className="gradient-45deg-indigo-purple"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <MDBox className="content-wrapper-before" />
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {isMini ? null : (
          <>
            {/* MENU ICON */}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              onClick={handleMiniSidenav}
              sx={(theme) => ({
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: theme.zIndex.appBar + 1,
                left: {
                  xs: 8,           // mobile and small tablets
                  sm: 8,           // small screens
                  md: 8,           // tablets / iPads
                  lg: miniSidenav ? "80px" : "250px",  // only on large desktop screens
                },
                transition: "left 0.25s ease",
                ...navbarMobileMenu,
              })}
            >
              <Icon sx={iconsStyle} fontSize="medium">
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>

            {/* RIGHT SIDE */}
            <MDBox sx={(theme) => navbarRow(theme, { isMini })} width="100%">
              <MDBox
                display="flex"
                textAlign="right"
                alignItems="center"
                color={light ? "white" : "inherit"}
                width="100%"
              >
                <MDBox display="flex" justifyContent="flex-end" width="100%" alignItems="center">
                  <Link to="/user-profile">
                    <IconButton sx={navbarIconButton} size="small" disableRipple title={name}>
                      <UserAvatar name={name} size="sm" shadow="sm" />
                    </IconButton>
                  </Link>
                  <MDBox>
                    <MDTypography
                      ml={1}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        fontSize: "10px",
                      }}
                      component={Link}
                      onClick={handleLogOut}
                      to=""
                      title="LOG OUT"
                      display="block"
                      variant="caption"
                      color="white"
                      fontWeight="bold"
                    >
                      <Icon fontSize="small">logout_icon</Icon>
                      <p>LOG OUT</p>
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}

// DashboardNavbar.defaultProps = {
//   absolute: false,
//   light: false,
//   isMini: false,
// };

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  breadcrumbsTitle: PropTypes.string,
};

export default DashboardNavbar;
